import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HardHat, LogOut, Trash2, Shield, AlertCircle, Edit2, Check, X, FileText, Image as ImageIcon, Sparkles, LayoutGrid, List, ArrowUpDown, ChevronUp, ChevronDown, Eye, Maximize2, Minimize2 } from 'lucide-react';
import axios from 'axios';
import DragDropImageUpload from '../components/DragDropImageUpload';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== "undefined") ? process.env.REACT_APP_BACKEND_URL : "";
const API = `${BACKEND_URL}/api`;

const convertPdfToImages = (file) => {
  return new Promise((resolve, reject) => {
    if (!window.pdfjsLib) {
      reject(new Error("PDF.js library is not loaded yet. Please wait a moment and try again."));
      return;
    }
    
    const fileReader = new FileReader();
    fileReader.onload = function() {
      const typedarray = new Uint8Array(this.result);
      
      window.pdfjsLib.getDocument(typedarray).promise.then(pdf => {
        const numPages = pdf.numPages;
        const pagePromises = [];
        
        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          pagePromises.push(
            pdf.getPage(pageNum).then(page => {
              const viewport = page.getViewport({ scale: 2.0 });
              const canvas = document.createElement('canvas');
              canvas.width = viewport.width;
              canvas.height = viewport.height;
              
              const context = canvas.getContext('2d');
              const renderContext = {
                canvasContext: context,
                viewport: viewport
              };
              
              return page.render(renderContext).promise.then(() => {
                return new Promise((blobResolve) => {
                  canvas.toBlob((blob) => {
                    if (blob) {
                      const baseName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
                      const fileName = numPages > 1 ? `${baseName}_page_${pageNum}.jpg` : `${baseName}.jpg`;
                      const convertedFile = new File([blob], fileName, { type: 'image/jpeg' });
                      blobResolve(convertedFile);
                    } else {
                      blobResolve(null);
                    }
                  }, 'image/jpeg', 0.92);
                });
              });
            })
          );
        }
        
        Promise.all(pagePromises).then(results => {
          const files = results.filter(f => f !== null);
          resolve(files);
        }).catch(err => reject(err));
      }).catch(err => reject(err));
    };
    fileReader.onerror = () => reject(new Error("Failed to read PDF file"));
    fileReader.readAsArrayBuffer(file);
  });
};

const fetchImageAsFile = async (url, filename) => {
  const response = await fetch(url);
  const blob = await response.blob();
  const fileExt = filename.split('.').pop().split(/\#|\?/)[0] || 'jpg';
  const mimeType = blob.type || `image/${fileExt}`;
  return new File([blob], filename, { type: mimeType });
};

export const EmployeeUploadPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [employeeName, setEmployeeName] = useState('');
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  // Login form states
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Password setup states
  const [needSetup, setNeedSetup] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Products upload/management states
  const [uploads, setUploads] = useState([]);
  const [loadingUploads, setLoadingUploads] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  
  // Editing state for product names
  const [editingId, setEditingId] = useState(null);
  const [editNameValue, setEditNameValue] = useState('');
  const [editProductionTimeValue, setEditProductionTimeValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedUploads, setSelectedUploads] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [sortOrder, setSortOrder] = useState('desc');
  const [activeZoomImage, setActiveZoomImage] = useState(null);

  // Lightbox Pan and Zoom states
  const [zoomScale, setZoomScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Watermarking option states
  const [isWatermarkEnabled, setIsWatermarkEnabled] = useState(false);
  const [isWatermarkModalOpen, setIsWatermarkModalOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [watermarkActionType, setWatermarkActionType] = useState('upload'); // 'upload' or 'existing'
  const [watermarkText, setWatermarkText] = useState('');
  const [watermarkColor, setWatermarkColor] = useState('#FFFFFF');
  const [watermarkFont, setWatermarkFont] = useState('Oswald');
  const [watermarkSize, setWatermarkSize] = useState(3.5); // % of image width
  const [watermarkPadding, setWatermarkPadding] = useState(3.0); // % of image width
  const [watermarkOpacity, setWatermarkOpacity] = useState(65); // 0-100
  const [watermarkLocation, setWatermarkLocation] = useState('bottom-right');
  const [watermarkBorder, setWatermarkBorder] = useState(true);
  const [watermarkBorderColor, setWatermarkBorderColor] = useState('#000000');
  const [watermarkBorderSize, setWatermarkBorderSize] = useState(15); // % of font size
  const [previewImage, setPreviewImage] = useState(null);
  const previewCanvasRef = useRef(null);
  const [isPreviewZoomed, setIsPreviewZoomed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Set default zoom based on device screen width when modal opens
  useEffect(() => {
    if (isWatermarkModalOpen) {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setIsPreviewZoomed(mobile);
    }
  }, [isWatermarkModalOpen]);

  // Helper to calculate translation and scale for centering watermark corner zoom
  const getPreviewTransform = () => {
    if (!isPreviewZoomed) return { transform: 'translate(0, 0) scale(1)', transformOrigin: 'center' };
    
    switch (watermarkLocation) {
      case 'top-left':
        return {
          transform: 'translate(3%, 11%) scale(2.5)',
          transformOrigin: 'top left'
        };
      case 'top-right':
        return {
          transform: 'translate(-3%, 11%) scale(2.5)',
          transformOrigin: 'top right'
        };
      case 'bottom-left':
        return {
          transform: 'translate(3%, -11%) scale(2.5)',
          transformOrigin: 'bottom left'
        };
      case 'bottom-right':
        return {
          transform: 'translate(-3%, -11%) scale(2.5)',
          transformOrigin: 'bottom right'
        };
      default:
        return { transform: 'translate(0, 0) scale(1)', transformOrigin: 'center' };
    }
  };

  // Dynamically load PDF.js script from CDN
  useEffect(() => {
    if (window.pdfjsLib) return;
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js';
    };
    document.head.appendChild(script);
  }, []);

  // Load saved watermark preferences based on employeeName from localStorage
  useEffect(() => {
    if (employeeName) {
      const capitalized = employeeName.charAt(0).toUpperCase() + employeeName.slice(1);
      
      const savedText = localStorage.getItem(`watermark_text_${employeeName}`);
      setWatermarkText(savedText !== null ? savedText : capitalized);

      setIsWatermarkEnabled(false);

      const savedColor = localStorage.getItem(`watermark_color_${employeeName}`);
      if (savedColor !== null) setWatermarkColor(savedColor);

      const savedFont = localStorage.getItem(`watermark_font_${employeeName}`);
      if (savedFont !== null) setWatermarkFont(savedFont);

      const savedSize = localStorage.getItem(`watermark_size_${employeeName}`);
      if (savedSize !== null) setWatermarkSize(parseFloat(savedSize));

      const savedPadding = localStorage.getItem(`watermark_padding_${employeeName}`);
      if (savedPadding !== null) setWatermarkPadding(parseFloat(savedPadding));

      const savedOpacity = localStorage.getItem(`watermark_opacity_${employeeName}`);
      if (savedOpacity !== null) setWatermarkOpacity(parseInt(savedOpacity));

      const savedLocation = localStorage.getItem(`watermark_location_${employeeName}`);
      if (savedLocation !== null) setWatermarkLocation(savedLocation);

      const savedBorder = localStorage.getItem(`watermark_border_${employeeName}`);
      if (savedBorder !== null) setWatermarkBorder(savedBorder === 'true');

      const savedBorderColor = localStorage.getItem(`watermark_border_color_${employeeName}`);
      if (savedBorderColor !== null) setWatermarkBorderColor(savedBorderColor);

      const savedBorderSize = localStorage.getItem(`watermark_border_size_${employeeName}`);
      if (savedBorderSize !== null) setWatermarkBorderSize(parseInt(savedBorderSize));
    }
  }, [employeeName]);

  // Auto-save watermark preferences to localStorage on change
  useEffect(() => {
    if (!employeeName) return;
    localStorage.setItem(`watermark_text_${employeeName}`, watermarkText);
    localStorage.setItem(`watermark_color_${employeeName}`, watermarkColor);
    localStorage.setItem(`watermark_font_${employeeName}`, watermarkFont);
    localStorage.setItem(`watermark_size_${employeeName}`, watermarkSize.toString());
    localStorage.setItem(`watermark_padding_${employeeName}`, watermarkPadding.toString());
    localStorage.setItem(`watermark_opacity_${employeeName}`, watermarkOpacity.toString());
    localStorage.setItem(`watermark_location_${employeeName}`, watermarkLocation);
    localStorage.setItem(`watermark_border_${employeeName}`, watermarkBorder ? 'true' : 'false');
    localStorage.setItem(`watermark_border_color_${employeeName}`, watermarkBorderColor);
    localStorage.setItem(`watermark_border_size_${employeeName}`, watermarkBorderSize.toString());
  }, [
    employeeName,
    watermarkText,
    watermarkColor,
    watermarkFont,
    watermarkSize,
    watermarkPadding,
    watermarkOpacity,
    watermarkLocation,
    watermarkBorder,
    watermarkBorderColor,
    watermarkBorderSize
  ]);

  // Reset zoom states when opening/closing zoom image
  useEffect(() => {
    setZoomScale(1);
    setPanOffset({ x: 0, y: 0 });
    setIsDragging(false);
  }, [activeZoomImage]);
  
  // Verify session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await axios.get(`${API}/employee/auth`, { withCredentials: true });
        if (response.data.authenticated) {
          setIsAuthenticated(true);
          setEmployeeName(response.data.username);
          loadEmployeeProducts();
        }
      } catch (err) {
        console.error("Session check failed", err);
      } finally {
        setCheckingAuth(false);
      }
    };
    checkSession();
  }, []);

  // Watermark Live Preview Canvas Drawer
  useEffect(() => {
    if (!previewCanvasRef.current || !previewImage) return;
    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Fit canvas dimensions to scaled preview (max width 600)
    const maxPreviewWidth = 600;
    const scale = Math.min(1, maxPreviewWidth / previewImage.naturalWidth);
    const canvasWidth = previewImage.naturalWidth * scale;
    const canvasHeight = previewImage.naturalHeight * scale;
    
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    
    ctx.drawImage(previewImage, 0, 0, canvasWidth, canvasHeight);
    
    // Relative font size & padding to canvas width
    const fontSize = Math.max(10, canvasWidth * (watermarkSize / 100));
    const padding = canvasWidth * (watermarkPadding / 100);
    
    ctx.font = `bold ${fontSize}px ${watermarkFont}`;
    ctx.textBaseline = 'top';
    
    let x = 0;
    let y = 0;
    let align = 'left';
    
    if (watermarkLocation === 'top-left') {
      x = padding;
      y = padding;
      align = 'left';
    } else if (watermarkLocation === 'top-right') {
      x = canvasWidth - padding;
      y = padding;
      align = 'right';
    } else if (watermarkLocation === 'bottom-left') {
      x = padding;
      y = canvasHeight - padding - fontSize;
      align = 'left';
    } else if (watermarkLocation === 'bottom-right') {
      x = canvasWidth - padding;
      y = canvasHeight - padding - fontSize;
      align = 'right';
    }
    
    ctx.textAlign = align;
    ctx.globalAlpha = watermarkOpacity / 100;
    
    if (watermarkBorder) {
      ctx.strokeStyle = watermarkBorderColor;
      ctx.lineWidth = Math.max(1, fontSize * (watermarkBorderSize / 100));
      ctx.strokeText(watermarkText, x, y);
    }
    
    ctx.fillStyle = watermarkColor;
    ctx.fillText(watermarkText, x, y);
    ctx.globalAlpha = 1.0;
  }, [
    previewImage,
    watermarkText,
    watermarkColor,
    watermarkFont,
    watermarkSize,
    watermarkPadding,
    watermarkOpacity,
    watermarkLocation,
    watermarkBorder,
    watermarkBorderColor,
    watermarkBorderSize
  ]);

  // Full-resolution Watermarking and resizing processor
  const applyWatermarkToFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const maxDim = 1920;
          let width = img.naturalWidth;
          let height = img.naturalHeight;
          
          if (width > maxDim || height > maxDim) {
            const ratio = Math.min(maxDim / width, maxDim / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const fontSize = Math.max(12, width * (watermarkSize / 100));
          const padding = width * (watermarkPadding / 100);
          
          ctx.font = `bold ${fontSize}px ${watermarkFont}`;
          ctx.textBaseline = 'top';
          
          let x = 0;
          let y = 0;
          let align = 'left';
          
          if (watermarkLocation === 'top-left') {
            x = padding;
            y = padding;
            align = 'left';
          } else if (watermarkLocation === 'top-right') {
            x = width - padding;
            y = padding;
            align = 'right';
          } else if (watermarkLocation === 'bottom-left') {
            x = padding;
            y = height - padding - fontSize;
            align = 'left';
          } else if (watermarkLocation === 'bottom-right') {
            x = width - padding;
            y = height - padding - fontSize;
            align = 'right';
          }
          
          ctx.textAlign = align;
          ctx.globalAlpha = watermarkOpacity / 100;
          
          if (watermarkBorder) {
            ctx.strokeStyle = watermarkBorderColor;
            ctx.lineWidth = Math.max(1.5, fontSize * (watermarkBorderSize / 100));
            ctx.strokeText(watermarkText, x, y);
          }
          
          ctx.fillStyle = watermarkColor;
          ctx.fillText(watermarkText, x, y);
          
          // Force conversion to jpeg for optimized size, unless it's a gif or svg
          const mimeType = 'image/jpeg';
          const extension = 'jpg';
 
          canvas.toBlob((blob) => {
            if (blob) {
              const baseName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
              const watermarkedFile = new File([blob], `${baseName}_watermarked.${extension}`, { type: mimeType });
              resolve(watermarkedFile);
            } else {
              reject(new Error("Blob creation failed"));
            }
          }, mimeType, 0.82);
        };
        img.onerror = () => reject(new Error("Image load failed"));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error("File read failed"));
      reader.readAsDataURL(file);
    });
  };

  const handleUploadIntercept = async (input) => {
    if (!input) return;
    const initialFiles = Array.isArray(input) ? input : [input];
    if (initialFiles.length === 0) return;
    
    setUploadingFile(true);
    setUploadProgress({ current: 0, total: initialFiles.length, filename: "Processing files..." });
    
    const processedFiles = [];
    try {
      for (let i = 0; i < initialFiles.length; i++) {
        const file = initialFiles[i];
        if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
          setUploadProgress({ current: i, total: initialFiles.length, filename: `Converting PDF: ${file.name}` });
          try {
            const convertedList = await convertPdfToImages(file);
            processedFiles.push(...convertedList);
            toast.success(`Converted PDF "${file.name}" to ${convertedList.length} image(s)!`);
          } catch (err) {
            console.error("PDF conversion failed for " + file.name, err);
            toast.error(`Failed to convert PDF "${file.name}": ` + err.message);
          }
        } else {
          processedFiles.push(file);
        }
      }
    } finally {
      setUploadProgress(null);
      setUploadingFile(false);
    }
    
    if (processedFiles.length === 0) return;
    
    if (isWatermarkEnabled) {
      setWatermarkActionType('upload');
      setPendingFiles(processedFiles);
      const file = processedFiles[0];
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        setPreviewImage(img);
        setIsWatermarkModalOpen(true);
        URL.revokeObjectURL(url);
      };
      img.src = url;
    } else {
      await handleUpload(processedFiles);
    }
  };

  const handleConfirmWatermark = async () => {
    setIsWatermarkModalOpen(false);
    setUploadingFile(true);
    setUploadProgress({ current: 0, total: pendingFiles.length, filename: "Watermarking images..." });
    
    let processedFiles = [];
    
    for (let i = 0; i < pendingFiles.length; i++) {
      const file = pendingFiles[i];
      setUploadProgress({ current: i, total: pendingFiles.length, filename: `Watermarking: ${file.name}` });
      try {
        const processed = await applyWatermarkToFile(file);
        // Transfer metadata
        processed.draftId = file.draftId;
        processed.productName = file.productName;
        processed.productionTime = file.productionTime;
        processedFiles.push(processed);
      } catch (err) {
        console.error("Watermark overlay failed for " + file.name, err);
        toast.error(`Could not apply watermark to ${file.name}, uploading raw image.`);
        // Transfer metadata even in error fallback
        file.draftId = file.draftId;
        file.productName = file.productName;
        file.productionTime = file.productionTime;
        processedFiles.push(file);
      }
    }
    
    setPendingFiles([]);
    setPreviewImage(null);

    if (watermarkActionType === 'existing') {
      await handleUploadExistingWatermarked(processedFiles);
    } else {
      await handleUpload(processedFiles);
    }
  };

  const handleCancelWatermark = () => {
    setIsWatermarkModalOpen(false);
    setPendingFiles([]);
    setPreviewImage(null);
  };

  // Pan and Zoom Interactive event handlers
  const handleMouseDown = (e) => {
    if (zoomScale <= 1) return; // Only allow pan when zoomed in
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    setPanOffset({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (zoomScale <= 1) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX - panOffset.x, y: touch.clientY - panOffset.y });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    setPanOffset({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y
    });
  };

  const loadEmployeeProducts = async () => {
    setLoadingUploads(true);
    try {
      const response = await axios.get(`${API}/employee/products`, { withCredentials: true });
      setUploads(response.data);
    } catch (err) {
      console.error("Failed to load products", err);
      toast.error("Failed to load your uploaded images");
    } finally {
      setLoadingUploads(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const response = await axios.post(`${API}/employee/auth`, {
        username,
        password
      }, { withCredentials: true });
      
      if (response.data.need_setup) {
        setNeedSetup(true);
        toast.info(response.data.detail || "Welcome! Please create a password.");
      } else {
        setIsAuthenticated(true);
        setEmployeeName(response.data.username);
        toast.success(`Welcome back, ${response.data.username}!`);
        loadEmployeeProducts();
      }
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Invalid login credentials. Try again.");
      toast.error("Authentication failed");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleCreatePassword = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    if (newPassword !== confirmPassword) {
      setLoginError("Passwords do not match.");
      setLoginLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setLoginError("Password must be at least 6 characters long.");
      setLoginLoading(false);
      return;
    }

    if (!/[A-Z]/.test(newPassword)) {
      setLoginError("Password must contain at least one capitalized character.");
      setLoginLoading(false);
      return;
    }

    if (!/[0-9]/.test(newPassword)) {
      setLoginError("Password must contain at least one number.");
      setLoginLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/employee/auth`, {
        username,
        password: newPassword,
        action: 'create_password'
      }, { withCredentials: true });
      
      setIsAuthenticated(true);
      setEmployeeName(response.data.username);
      toast.success("Password created successfully!");
      setNeedSetup(false);
      setNewPassword('');
      setConfirmPassword('');
      loadEmployeeProducts();
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Failed to create password. Please try again.");
      toast.error("Password creation failed");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/employee/auth`, { action: 'logout' }, { withCredentials: true });
      setIsAuthenticated(false);
      setEmployeeName('');
      setUploads([]);
      setUsername('');
      setPassword('');
      toast.success("Logged out successfully");
    } catch (err) {
      console.error("Logout failed", err);
      toast.error("Logout failed");
    }
  };

  const handleUpload = async (input) => {
    if (!input) return;
    
    // Normalize input to an array of files
    const files = Array.isArray(input) ? input : [input];
    if (files.length === 0) return;

    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    
    const compressImageClientSide = (file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const maxDim = 1920;
            let width = img.naturalWidth;
            let height = img.naturalHeight;
            
            if (width <= maxDim && height <= maxDim && file.type === 'image/jpeg' && file.size < 500 * 1024) {
              // Already optimal, bypass Canvas processing
              resolve(file);
              return;
            }
            
            if (width > maxDim || height > maxDim) {
              const ratio = Math.min(maxDim / width, maxDim / height);
              width = Math.round(width * ratio);
              height = Math.round(height * ratio);
            }
            
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            
            canvas.toBlob((blob) => {
              if (blob) {
                const baseName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
                const compressedFile = new File([blob], `${baseName}_optimized.jpg`, { type: 'image/jpeg' });
                resolve(compressedFile);
              } else {
                resolve(file);
              }
            }, 'image/jpeg', 0.80);
          };
          img.onerror = () => resolve(file);
          img.src = e.target.result;
        };
        reader.onerror = () => resolve(file);
        reader.readAsDataURL(file);
      });
    };

    const executeWithRetry = async (fn, retries = 2, delay = 1000) => {
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          return await fn();
        } catch (err) {
          if (attempt === retries) throw err;
          console.warn(`Request attempt failed. Retrying in ${delay}ms... (${retries - attempt} attempts left)`, err);
          await sleep(delay);
        }
      }
    };

    setUploadingFile(true);
    setUploadProgress({ current: 0, total: files.length, filename: files[0].name });
    let successCount = 0;
    let failCount = 0;

    const concurrencyLimit = 3;
    let completedCount = 0;
    const activeFiles = [];

    const executeUploadTask = async (file) => {
      activeFiles.push(file.name);
      setUploadProgress({
        current: completedCount,
        total: files.length,
        filename: `Uploading: ${activeFiles.slice(0, 2).join(', ')}${activeFiles.length > 2 ? '...' : ''}`
      });

      try {
        const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
        const optimizedFile = isSvg ? file : await compressImageClientSide(file);

        // Validate size (max 10MB)
        if (optimizedFile.size > 10 * 1024 * 1024) {
          toast.error(`"${file.name}" is too large (max 10MB)`);
          failCount++;
          return;
        }

        const formData = new FormData();
        formData.append('file', optimizedFile);
        
        // 1. Upload to the compression service with retry
        const uploadRes = await executeWithRetry(() => 
          axios.post(`${API}/employee/upload-image`, formData, {
            withCredentials: true,
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })
        );
        
        const imageUrl = uploadRes.data.image_url;

        // 2. Save image details in the employee products database table with retry
        await executeWithRetry(() => 
          axios.post(`${API}/employee/products`, {
            image_url: imageUrl,
            product_name: "" // Initially blank as requested
          }, { withCredentials: true })
        );

        successCount++;
      } catch (err) {
        console.error(`Upload failed for ${file.name}`, err);
        toast.error(err.response?.data?.detail || `Failed to upload ${file.name}`);
        failCount++;
      } finally {
        completedCount++;
        activeFiles.splice(activeFiles.indexOf(file.name), 1);
        setUploadProgress({
          current: completedCount,
          total: files.length,
          filename: activeFiles.length > 0 
            ? `Uploading: ${activeFiles.slice(0, 2).join(', ')}${activeFiles.length > 2 ? '...' : ''}`
            : 'Finishing...'
        });
      }
    };

    try {
      const queue = [...files];
      const pool = [];
      
      while (queue.length > 0) {
        while (pool.length < concurrencyLimit && queue.length > 0) {
          const file = queue.shift();
          const promise = executeUploadTask(file).then(() => {
            pool.splice(pool.indexOf(promise), 1);
          });
          pool.push(promise);
        }
        
        if (pool.length > 0) {
          await Promise.race(pool);
        }
      }
      
      await Promise.all(pool);

      if (successCount > 0) {
        toast.success(files.length > 1 
          ? `Successfully uploaded ${successCount} of ${files.length} product images!`
          : "Product image uploaded successfully!"
        );
        loadEmployeeProducts();
      } else if (failCount > 0) {
        toast.error("Failed to upload selected image(s).");
      }
    } finally {
      setUploadProgress(null);
      setUploadingFile(false);
    }
  };

  const handleSaveName = async (id) => {
    try {
      await axios.put(`${API}/employee/products?id=${id}`, {
        product_name: editNameValue,
        production_time: editProductionTimeValue || null
      }, { withCredentials: true });
      
      toast.success("Product details updated!");
      setEditingId(null);
      loadEmployeeProducts();
    } catch (err) {
      console.error("Update failed", err);
      toast.error("Failed to update product details");
    }
  };

  const handleWatermarkSelected = async () => {
    if (selectedUploads.length === 0) return;

    setUploadingFile(true);
    setUploadProgress({ current: 0, total: selectedUploads.length, filename: "Loading draft images..." });

    const downloadedFiles = [];
    try {
      for (let i = 0; i < selectedUploads.length; i++) {
        const id = selectedUploads[i];
        const item = uploads.find(x => x.id === id);
        if (!item) continue;

        setUploadProgress({ current: i, total: selectedUploads.length, filename: `Loading: ${item.product_name || 'Unnamed Draft'}` });

        try {
          const fetchUrl = item.image_url.startsWith('http') ? item.image_url : `${BACKEND_URL}${item.image_url}`;
          const originalName = item.image_url.substring(item.image_url.lastIndexOf('/') + 1);
          const file = await fetchImageAsFile(fetchUrl, originalName);
          
          file.draftId = id;
          file.productName = item.product_name;
          file.productionTime = item.production_time;

          downloadedFiles.push(file);
        } catch (err) {
          console.error("Failed to load draft for watermarking", err);
        }
      }
    } finally {
      setUploadProgress(null);
      setUploadingFile(false);
    }

    if (downloadedFiles.length === 0) {
      toast.error("Could not load any selected drafts for watermarking.");
      return;
    }

    setPendingFiles(downloadedFiles);
    setWatermarkActionType('existing');

    const firstFile = downloadedFiles[0];
    const url = URL.createObjectURL(firstFile);
    const img = new Image();
    img.onload = () => {
      setPreviewImage(img);
      setIsWatermarkModalOpen(true);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  const handleUploadExistingWatermarked = async (files) => {
    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    const executeWithRetry = async (fn, retries = 2, delay = 1000) => {
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          return await fn();
        } catch (err) {
          if (attempt === retries) throw err;
          console.warn(`Watermark sync attempt failed. Retrying in ${delay}ms... (${retries - attempt} attempts left)`, err);
          await sleep(delay);
        }
      }
    };

    setUploadingFile(true);
    let successCount = 0;
    
    const concurrencyLimit = 3;
    let completedCount = 0;
    const activeFiles = [];

    const executeWatermarkTask = async (file) => {
      const id = file.draftId;
      if (!id) return;

      activeFiles.push(file.name);
      setUploadProgress({
        current: completedCount,
        total: files.length,
        filename: `Updating: ${activeFiles.slice(0, 2).join(', ')}${activeFiles.length > 2 ? '...' : ''}`
      });

      try {
        const formData = new FormData();
        formData.append('file', file);

        // 1. Upload watermarked image with retry
        const uploadRes = await executeWithRetry(() => 
          axios.post(`${API}/employee/upload-image`, formData, {
            withCredentials: true,
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })
        );
        
        const newImageUrl = uploadRes.data.image_url;

        // 2. Put back into product row with retry
        await executeWithRetry(() => 
          axios.put(`${API}/employee/products?id=${id}`, {
            image_url: newImageUrl,
            product_name: file.productName,
            production_time: file.productionTime
          }, { withCredentials: true })
        );

        successCount++;
      } catch (err) {
        console.error(`Overwriting failed for draft ${id}`, err);
        toast.error(`Failed to update watermarked version of "${file.productName || 'Unnamed Draft'}"`);
      } finally {
        completedCount++;
        activeFiles.splice(activeFiles.indexOf(file.name), 1);
        setUploadProgress({
          current: completedCount,
          total: files.length,
          filename: activeFiles.length > 0 
            ? `Updating: ${activeFiles.slice(0, 2).join(', ')}${activeFiles.length > 2 ? '...' : ''}`
            : 'Finishing...'
        });
      }
    };

    try {
      const queue = [...files];
      const pool = [];
      
      while (queue.length > 0) {
        while (pool.length < concurrencyLimit && queue.length > 0) {
          const file = queue.shift();
          const promise = executeWatermarkTask(file).then(() => {
            pool.splice(pool.indexOf(promise), 1);
          });
          pool.push(promise);
        }
        
        if (pool.length > 0) {
          await Promise.race(pool);
        }
      }
      
      await Promise.all(pool);

      if (successCount > 0) {
        toast.success(`Successfully watermarked and updated ${successCount} draft(s) in-place!`);
        setSelectedUploads([]);
        loadEmployeeProducts();
      }
    } finally {
      setUploadProgress(null);
      setUploadingFile(false);
    }
  };

  const handleToggleSelectUpload = (id) => {
    setSelectedUploads(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleDeleteClick = (id, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setDeleteTarget(id);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    const idsToDelete = Array.isArray(deleteTarget) ? deleteTarget : [deleteTarget];
    if (idsToDelete.length === 0) return;

    setUploadingFile(true);
    
    try {
      const idsStr = idsToDelete.join(',');
      const res = await axios.delete(`${API}/employee/products?id=${idsStr}`, { withCredentials: true });
      const deletedCount = res.data.deleted_count || idsToDelete.length;

      toast.success(idsToDelete.length > 1
        ? `Deleted ${deletedCount} product drafts successfully`
        : "Image deleted successfully"
      );
      setSelectedUploads([]); // Reset selection
      setDeleteTarget(null);
      loadEmployeeProducts();
    } catch (err) {
      console.error("Bulk deletion failed", err);
      toast.error("Failed to delete all selected images");
    } finally {
      setUploadingFile(false);
    }
  };

  // Sort uploads dynamically
  const sortedUploads = [...uploads].sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white">
        <div className="flex flex-col items-center gap-3">
          <HardHat className="w-12 h-12 text-[#FF9900] animate-bounce" />
          <p className="text-gray-400 font-medium uppercase tracking-wider text-sm">Verifying Shop Session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <Toaster position="top-right" theme="dark" />
      
      {/* HEADER BAR */}
      <header className="bg-[#121212] border-b border-[#27272A] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#FF9900] flex items-center justify-center rounded-sm">
            <HardHat className="w-6 h-6 text-black" />
          </div>
          <div>
            <h1 className="text-md sm:text-xl font-bold uppercase tracking-tight text-[#FF9900]">Eastend Iron Shop</h1>
            <p className="text-xs text-gray-400">Internal Product Upload Hub</p>
          </div>
        </div>
        
        {isAuthenticated && (
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-red-950/40 text-red-400 border border-red-900/50 px-3 py-2 font-bold uppercase text-xs hover:bg-red-900 hover:text-white transition-all duration-300"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
        )}
      </header>

      {/* BODY CONTENT */}
      <main className="flex-1 flex flex-col p-4 sm:p-8 max-w-5xl mx-auto w-full">
        <AnimatePresence mode="wait">
          {!isAuthenticated ? (
            /* LOGIN CARD */
            <motion.div
              key="login"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="flex-1 flex items-center justify-center py-12"
            >
              <div className="w-full max-w-md bg-[#121212] border border-[#27272A] p-8 shadow-xl">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-[#FF9900]/10 border border-[#FF9900]/30 rounded-full flex items-center justify-center">
                    <Shield className="w-8 h-8 text-[#FF9900]" />
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-center text-[#FF9900] uppercase tracking-wide mb-2">Shop Member Sign In</h2>
                <p className="text-sm text-gray-500 text-center mb-8">Log in once. Your session will remain permanently active on this device.</p>

                {loginError && (
                  <div className="bg-red-950/20 border border-red-900/50 text-red-400 p-4 mb-6 flex items-start gap-3 text-sm">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>{loginError}</span>
                  </div>
                )}

                {!needSetup ? (
                  <form onSubmit={handleLogin} className="space-y-6">
                    <div>
                      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Username</label>
                      <input
                        type="text"
                        required
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Input your username here"
                        className="w-full bg-[#1E1E1E] border border-[#27272A] text-white px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Password</label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full bg-[#1E1E1E] border border-[#27272A] text-white px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loginLoading}
                      className="w-full bg-[#FF9900] text-black font-bold uppercase py-4 tracking-wider hover:bg-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loginLoading ? 'Authenticating...' : 'Sign In'}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleCreatePassword} className="space-y-6">
                    <div className="p-3 bg-[#FF9900]/10 border border-[#FF9900]/20 rounded-sm text-xs text-gray-300 leading-relaxed">
                      Welcome <span className="text-white font-bold">{username}</span>! This is your first login. Please create a password (minimum 6 characters, including 1 capitalized character and 1 number).
                    </div>
                    
                    <div>
                      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Create Password</label>
                      <input
                        type="password"
                        required
                        minLength={6}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="New Password"
                        className="w-full bg-[#1E1E1E] border border-[#27272A] text-white px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Confirm Password</label>
                      <input
                        type="password"
                        required
                        minLength={6}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm Password"
                        className="w-full bg-[#1E1E1E] border border-[#27272A] text-white px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors"
                      />
                    </div>

                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => {
                          setNeedSetup(false);
                          setLoginError('');
                          setNewPassword('');
                          setConfirmPassword('');
                        }}
                        className="flex-1 bg-[#1E1E1E] border border-[#27272A] text-gray-400 font-bold uppercase py-4 tracking-wider hover:text-white transition-all duration-300"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loginLoading}
                        className="flex-1 bg-[#FF9900] text-black font-bold uppercase py-4 tracking-wider hover:bg-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loginLoading ? 'Saving...' : 'Set Password'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          ) : (
            /* UPLOAD & MANAGEMENT PANEL */
            <motion.div
              key="panel"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-8"
            >
              {/* MEMBER GREETING & UPLOAD BOX */}
              <div className="bg-[#121212] border border-[#27272A] p-6 sm:p-8">
                <div className="flex items-center gap-2 text-gray-400 mb-4 text-xs font-bold uppercase tracking-wider">
                  <Sparkles className="w-4 h-4 text-[#FF9900] animate-pulse" />
                  <span>Logged in as Member:</span>
                  <span className="text-white font-black">{employeeName}</span>
                </div>
                
                <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight mb-2">Upload Workshop Product Photos</h2>
                <p className="text-xs sm:text-sm text-gray-400 mb-6">
                  Drag and drop your photos here, or click to browse. Images will be automatically optimized and compressed for peak website speed.
                </p>

                <DragDropImageUpload
                  value=""
                  onChange={() => {}}
                  onUpload={handleUploadIntercept}
                  buttonText={uploadingFile ? "Uploading Photos..." : "Select & Upload Photos"}
                  id="employee-image-upload"
                  multiple={true}
                />

                <div className="mt-4 flex items-center gap-3 bg-[#1A1A1A] border border-[#27272A] p-4 rounded-sm">
                  <label className="flex items-center gap-3 cursor-pointer group select-none">
                    <input
                      type="checkbox"
                      checked={isWatermarkEnabled}
                      onChange={(e) => setIsWatermarkEnabled(e.target.checked)}
                      className="peer sr-only"
                    />
                    <div className="w-5 h-5 border-2 border-white/60 bg-[#121212] group-hover:border-[#FF9900] peer-checked:border-[#FF9900] peer-checked:bg-[#FF9900] flex items-center justify-center transition-all duration-300 rounded-sm">
                      <Check className="w-3.5 h-3.5 text-black font-black opacity-0 peer-checked:opacity-100 transition-opacity" />
                    </div>
                    <div className="flex flex-col text-left">
                      <span className="text-sm font-bold uppercase tracking-wider text-gray-300 group-hover:text-white transition-colors">
                        Add Watermark to Photo(s)
                      </span>
                      <span className="text-xs text-gray-500">
                        Overlay your signature text at the corner of uploaded photos.
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              {/* DRAFTS LIST VIEW */}
              <div className="space-y-4">
                {uploadProgress && (
                  <div className="bg-[#1C1C1E] border border-[#27272A] p-4 sm:p-5 rounded-sm space-y-3 mb-6">
                    <div className="flex items-center justify-between text-xs font-bold uppercase tracking-wider">
                      <div className="flex items-center gap-2 text-gray-400">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#FF9900] animate-pulse" />
                        <span>
                          {uploadProgress.filename.startsWith("Watermarking") 
                            ? "Applying Custom Watermark..." 
                            : `Uploading Draft ${uploadProgress.current + 1} of ${uploadProgress.total}`
                          }
                        </span>
                      </div>
                      <span className="text-[#FF9900]">
                        {uploadProgress.total > 0 
                          ? Math.round((uploadProgress.current / uploadProgress.total) * 100) 
                          : 0
                        }%
                      </span>
                    </div>
                    <div className="w-full bg-[#121212] h-2 rounded-full overflow-hidden border border-white/5">
                      <div 
                        className="bg-[#FF9900] h-full transition-all duration-300 ease-out rounded-full"
                        style={{ width: `${uploadProgress.total > 0 ? (uploadProgress.current / uploadProgress.total) * 100 : 0}%` }}
                      />
                    </div>
                    <div className="text-[11px] text-gray-500 font-mono truncate">
                      {uploadProgress.filename}
                    </div>
                  </div>
                )}
                
                {/* Drafts Section Header & Controls */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#27272A] pb-4 mb-6">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-[#FF9900]" />
                    <h3 className="text-lg font-bold uppercase tracking-wide text-gray-300">Your Uploaded Product Drafts</h3>
                    {uploads.length > 0 && (
                      <span className="bg-[#1C1C1E] text-[#FF9900] text-xs font-bold px-3 py-1 border border-[#27272A] rounded-full">
                        {uploads.length} Uploads
                      </span>
                    )}
                  </div>
                  
                  {uploads.length > 0 && (
                    <div className="flex flex-wrap items-center gap-4">
                      {/* Select All Toggle Button */}
                      <button
                        onClick={() => {
                          if (selectedUploads.length === uploads.length) {
                            setSelectedUploads([]);
                          } else {
                            setSelectedUploads(uploads.map(x => x.id));
                          }
                        }}
                        className="bg-[#1A1A1A] border border-[#27272A] hover:border-[#FF9900] text-gray-400 hover:text-white px-4 py-2 font-bold uppercase text-xs tracking-wider transition-all duration-300 rounded-sm flex items-center gap-2"
                      >
                        <input
                          type="checkbox"
                          checked={uploads.length > 0 && selectedUploads.length === uploads.length}
                          readOnly
                          className="w-3.5 h-3.5 border border-white/40 bg-[#121212] checked:bg-[#FF9900] pointer-events-none accent-[#FF9900] rounded-sm"
                        />
                        <span>{selectedUploads.length === uploads.length ? 'Deselect All' : 'Select All'}</span>
                      </button>

                      {/* Watermark Selected Action */}
                      {selectedUploads.length > 0 && (
                        <button
                          onClick={handleWatermarkSelected}
                          className="bg-[#FF9900]/10 text-[#FF9900] border border-[#FF9900]/30 hover:bg-[#FF9900] hover:text-black px-4 py-2 font-bold uppercase text-xs tracking-wider transition-all duration-300 rounded-sm flex items-center gap-2"
                        >
                          <Sparkles className="w-4 h-4" />
                          <span>Watermark Selected ({selectedUploads.length})</span>
                        </button>
                      )}

                      {/* Bulk Deletion Trigger */}
                      {selectedUploads.length > 0 && (
                        <button
                          onClick={() => setDeleteTarget(selectedUploads)}
                          className="bg-red-950/40 text-red-400 border border-red-900/50 hover:bg-red-600 hover:text-white px-4 py-2 font-bold uppercase text-xs tracking-wider transition-all duration-300 rounded-sm flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Delete Selected ({selectedUploads.length})</span>
                        </button>
                      )}

                      {/* Sorting Controls */}
                      <div className="flex items-center bg-[#1A1A1A] border border-[#27272A] rounded-sm p-1">
                        <button
                          onClick={() => setSortOrder('desc')}
                          className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-all duration-300 rounded-sm flex items-center gap-1.5 ${
                            sortOrder === 'desc'
                              ? 'bg-[#FF9900] text-black'
                              : 'text-gray-400 hover:text-white'
                          }`}
                          title="Newest Uploads First"
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                          Newest
                        </button>
                        <button
                          onClick={() => setSortOrder('asc')}
                          className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-all duration-300 rounded-sm flex items-center gap-1.5 ${
                            sortOrder === 'asc'
                              ? 'bg-[#FF9900] text-black'
                              : 'text-gray-400 hover:text-white'
                          }`}
                          title="Oldest Uploads First"
                        >
                          <ChevronUp className="w-3.5 h-3.5" />
                          Oldest
                        </button>
                      </div>

                      {/* Layout View Toggles */}
                      <div className="flex items-center bg-[#1A1A1A] border border-[#27272A] rounded-sm p-1">
                        <button
                          onClick={() => setViewMode('grid')}
                          className={`p-1.5 transition-all duration-300 rounded-sm ${
                            viewMode === 'grid'
                              ? 'bg-[#FF9900] text-black'
                              : 'text-gray-400 hover:text-white'
                          }`}
                          title="Grid View"
                        >
                          <LayoutGrid className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setViewMode('table')}
                          className={`p-1.5 transition-all duration-300 rounded-sm ${
                            viewMode === 'table'
                              ? 'bg-[#FF9900] text-black'
                              : 'text-gray-400 hover:text-white'
                          }`}
                          title="Table List View"
                        >
                          <List className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {loadingUploads ? (
                  <div className="py-12 flex justify-center text-gray-500">
                    <p className="animate-pulse uppercase font-medium tracking-widest text-xs">Syncing Upload History...</p>
                  </div>
                ) : uploads.length === 0 ? (
                  <div className="bg-[#121212] border border-[#27272A] border-dashed p-12 text-center text-gray-500 rounded-sm">
                    <ImageIcon className="w-12 h-12 mx-auto mb-4 opacity-30 text-gray-400" />
                    <p className="text-sm font-medium uppercase tracking-wide">No images uploaded yet</p>
                    <p className="text-xs text-gray-600 mt-1">Photos you upload from the shop floor will appear here.</p>
                  </div>
                ) : viewMode === 'grid' ? (
                  /* Grid view mode */
                  <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
                    {sortedUploads.map(item => {
                      const isSelected = selectedUploads.includes(item.id);
                      return (
                        <motion.div
                          key={item.id}
                          layout
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className={`bg-[#121212] border overflow-hidden flex flex-col group transition-all duration-300 rounded-sm ${isSelected ? 'border-[#FF9900] ring-1 ring-[#FF9900]/10' : 'border-[#27272A]'}`}
                        >
                          {/* Image Frame */}
                          <div 
                            onClick={() => handleToggleSelectUpload(item.id)}
                            className="relative aspect-video bg-[#0a0a0a] overflow-hidden cursor-pointer"
                          >
                            <img
                              src={item.image_url}
                              alt={item.product_name || "Uploaded draft"}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                            
                            {/* Checkbox Trigger Top Left */}
                            <label className="absolute top-2 left-2 z-10 flex items-center justify-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleSelectUpload(item.id)}
                                className="peer sr-only"
                              />
                              <div className="w-5 h-5 border-2 border-white/60 bg-[#121212]/80 hover:border-[#FF9900] peer-checked:border-[#FF9900] peer-checked:bg-[#FF9900] flex items-center justify-center transition-all duration-300 rounded-sm shadow-md">
                                <Check className="w-3 h-3 text-black font-black opacity-0 peer-checked:opacity-100 transition-opacity" />
                              </div>
                            </label>

                            {/* View Button next to Checkbox */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveZoomImage(item.image_url);
                              }}
                              className="absolute top-2 left-9 z-10 flex items-center gap-1 bg-[#121212]/85 border border-white/10 hover:border-[#FF9900] hover:bg-[#FF9900] hover:text-black text-white text-[9px] font-extrabold px-2.5 py-1 transition-all duration-300 rounded-sm shadow-md"
                              title="View Image Details"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              VIEW
                            </button>

                            {/* Delete Draft Button Top Right */}
                            <button
                              onClick={(e) => handleDeleteClick(item.id, e)}
                              className="absolute top-2 right-2 bg-red-650 bg-red-950/80 border border-red-900/60 p-2 hover:bg-red-600 hover:text-white transition-all duration-300 rounded-sm opacity-0 group-hover:opacity-100 z-10 shadow-md"
                              title="Delete Draft"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>

                          {/* Card Info/Form */}
                          <div className="p-4 flex-1 flex flex-col justify-between gap-4">
                            <div className="space-y-2">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider block">
                                  Uploaded: {new Date(item.created_at).toLocaleDateString(undefined, {
                                    month: 'short', day: 'numeric', year: 'numeric'
                                  })}
                                </span>
                                {item.product_id ? (
                                  <span className="bg-green-950/40 text-green-400 text-[9px] font-bold px-2.5 py-0.5 border border-green-900/30 rounded-full tracking-wider uppercase">
                                    Live
                                  </span>
                                ) : (
                                  <span className="bg-gray-800/40 text-gray-400 text-[9px] font-bold px-2.5 py-0.5 border border-gray-700/30 rounded-full tracking-wider uppercase">
                                    Draft
                                  </span>
                                )}
                              </div>

                              {editingId === item.id ? (
                                <div className="flex flex-col gap-2 w-full">
                                  <div>
                                    <label className="block text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-1">Product Name</label>
                                    <input
                                      type="text"
                                      value={editNameValue}
                                      onChange={(e) => setEditNameValue(e.target.value)}
                                      className="w-full bg-[#1E1E1E] border border-[#FF9900] text-sm px-3 py-2 text-white focus:outline-none"
                                      placeholder="Enter product name"
                                      autoFocus
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-[10px] text-gray-500 font-bold uppercase tracking-wider mb-1">Production Time (Days)</label>
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="number"
                                        min="0"
                                        value={editProductionTimeValue}
                                        onChange={(e) => setEditProductionTimeValue(e.target.value)}
                                        className="w-20 bg-[#1E1E1E] border border-[#27272A] text-sm px-3 py-2 text-white focus:border-[#FF9900] focus:outline-none"
                                        placeholder="e.g. 5"
                                      />
                                      <span className="text-[#FF9900] text-xs font-bold uppercase tracking-wider">Day</span>
                                    </div>
                                  </div>
                                  <div className="flex gap-2 mt-1">
                                    <button
                                      onClick={() => handleSaveName(item.id)}
                                      className="flex-1 bg-green-800 text-white py-1.5 px-3 hover:bg-green-650 transition-colors flex items-center justify-center gap-1.5 font-bold uppercase text-xs tracking-wider rounded-sm"
                                      title="Save Details"
                                    >
                                      <Check className="w-3.5 h-3.5" /> Save
                                    </button>
                                    <button
                                      onClick={() => setEditingId(null)}
                                      className="flex-1 bg-gray-800 text-gray-400 py-1.5 px-3 hover:bg-gray-700 transition-colors flex items-center justify-center gap-1.5 font-bold uppercase text-xs tracking-wider rounded-sm"
                                      title="Cancel"
                                    >
                                      <X className="w-3.5 h-3.5" /> Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <h4 className="text-sm font-bold text-white truncate uppercase tracking-tight">
                                        {item.product_name || <span className="text-gray-600 italic normal-case">Unnamed Draft</span>}
                                      </h4>
                                    </div>
                                    <button
                                      onClick={() => {
                                        setEditingId(item.id);
                                        setEditNameValue(item.product_name || '');
                                        setEditProductionTimeValue(item.production_time !== null && item.production_time !== undefined ? item.production_time.toString() : '');
                                      }}
                                      className="text-gray-500 hover:text-[#FF9900] transition-colors p-1"
                                      title="Edit Product Details"
                                    >
                                      <Edit2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                  {item.production_time && (
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[9px] uppercase font-bold tracking-wider text-gray-500">Est. Time:</span>
                                      <span className="text-[10px] font-bold text-[#FF9900] bg-[#FF9900]/5 border border-[#FF9900]/10 px-1.5 py-0.5 rounded-sm">
                                        {item.production_time} {parseInt(item.production_time) === 1 ? 'Day' : 'Days'}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  /* Table view mode */
                  <div className="overflow-x-auto bg-[#121212] border border-[#27272A] rounded-sm">
                    <table className="w-full text-left border-collapse text-sm">
                      <thead>
                        <tr className="border-b border-[#27272A] bg-[#171717]">
                          <th className="p-4 w-12 text-center">
                            <label className="flex items-center justify-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={sortedUploads.every(item => selectedUploads.includes(item.id))}
                                onChange={(e) => {
                                  const allChecked = e.target.checked;
                                  const visibleIds = sortedUploads.map(item => item.id);
                                  if (allChecked) {
                                    setSelectedUploads(prev => [...new Set([...prev, ...visibleIds])]);
                                  } else {
                                    setSelectedUploads(prev => prev.filter(id => !visibleIds.includes(id)));
                                  }
                                }}
                                className="peer sr-only"
                              />
                              <div className="w-4 h-4 border border-white/60 bg-[#121212] hover:border-[#FF9900] peer-checked:border-[#FF9900] peer-checked:bg-[#FF9900] flex items-center justify-center transition-all duration-300 rounded-sm">
                                <Check className="w-3 h-3 text-black font-black opacity-0 peer-checked:opacity-100 transition-opacity" />
                              </div>
                            </label>
                          </th>
                          <th className="p-4 font-extrabold text-xs text-gray-400 uppercase tracking-wider">Thumbnail</th>
                          <th className="p-4 font-extrabold text-xs text-gray-400 uppercase tracking-wider">Draft Name</th>
                          <th className="p-4 font-extrabold text-xs text-gray-400 uppercase tracking-wider">Production Time</th>
                          <th className="p-4 font-extrabold text-xs text-gray-400 uppercase tracking-wider">Upload Date</th>
                          <th className="p-4 font-extrabold text-xs text-gray-400 uppercase tracking-wider">Status</th>
                          <th className="p-4 font-extrabold text-xs text-gray-400 uppercase tracking-wider text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedUploads.map(item => {
                          const isSelected = selectedUploads.includes(item.id);
                          return (
                            <tr
                              key={item.id}
                              className={`border-b border-[#1C1C1E] hover:bg-[#161616] transition-colors ${
                                isSelected ? 'bg-[#FF9900]/5' : ''
                              }`}
                            >
                              <td className="p-4 text-center">
                                <label className="flex items-center justify-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleToggleSelectUpload(item.id)}
                                    className="peer sr-only"
                                  />
                                  <div className="w-4 h-4 border border-white/60 bg-[#121212] hover:border-[#FF9900] peer-checked:border-[#FF9900] peer-checked:bg-[#FF9900] flex items-center justify-center transition-all duration-300 rounded-sm">
                                    <Check className="w-3 h-3 text-black font-black opacity-0 peer-checked:opacity-100 transition-opacity" />
                                  </div>
                                </label>
                              </td>
                              <td className="p-4">
                                <div 
                                  onClick={() => setActiveZoomImage(item.image_url)}
                                  className="w-16 h-10 bg-[#0d0d0d] overflow-hidden border border-[#27272A] rounded-sm cursor-zoom-in relative group/thumb"
                                  title="Click to Zoom Image"
                                >
                                  <img src={item.image_url} alt="" className="w-full h-full object-cover group-hover/thumb:scale-110 transition-transform duration-300" />
                                  <div className="absolute inset-0 bg-black/45 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center">
                                    <Eye className="w-4 h-4 text-white" />
                                  </div>
                                </div>
                              </td>
                              <td className="p-4">
                                {editingId === item.id ? (
                                  <input
                                    type="text"
                                    value={editNameValue}
                                    onChange={(e) => setEditNameValue(e.target.value)}
                                    className="w-full bg-[#1E1E1E] border border-[#FF9900] text-xs px-2 py-1 text-white focus:outline-none uppercase"
                                    placeholder="Enter name"
                                    autoFocus
                                  />
                                ) : (
                                  <span className="font-bold text-white uppercase tracking-tight">
                                    {item.product_name || <span className="text-gray-600 italic normal-case">Unnamed Draft</span>}
                                  </span>
                                )}
                              </td>
                              <td className="p-4">
                                {editingId === item.id ? (
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number"
                                      min="0"
                                      value={editProductionTimeValue}
                                      onChange={(e) => setEditProductionTimeValue(e.target.value)}
                                      className="w-16 bg-[#1E1E1E] border border-[#27272A] text-xs px-2 py-1 text-white focus:border-[#FF9900] focus:outline-none"
                                      placeholder="optional"
                                    />
                                    <span className="text-[#FF9900] text-xs">Day</span>
                                  </div>
                                ) : (
                                  item.production_time ? (
                                    <span className="text-xs font-bold text-[#FF9900] bg-[#FF9900]/5 border border-[#FF9900]/10 px-1.5 py-0.5 rounded-sm">
                                      {item.production_time} {parseInt(item.production_time) === 1 ? 'Day' : 'Days'}
                                    </span>
                                  ) : (
                                    <span className="text-gray-600 italic">-</span>
                                  )
                                )}
                              </td>
                              <td className="p-4 text-xs font-bold text-gray-400 uppercase tracking-wider">
                                {new Date(item.created_at).toLocaleDateString(undefined, {
                                  month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                })}
                              </td>
                              <td className="p-4">
                                {item.product_id ? (
                                  <span className="bg-green-950/40 text-green-400 text-[9px] font-bold px-2.5 py-0.5 border border-green-900/30 rounded-full tracking-wider uppercase">
                                    Live
                                  </span>
                                ) : (
                                  <span className="bg-gray-800/40 text-gray-400 text-[9px] font-bold px-2.5 py-0.5 border border-gray-700/30 rounded-full tracking-wider uppercase">
                                    Draft
                                  </span>
                                )}
                              </td>
                              <td className="p-4 text-right">
                                {editingId === item.id ? (
                                  <div className="flex items-center justify-end gap-1.5">
                                    <button
                                      onClick={() => handleSaveName(item.id)}
                                      className="p-1.5 bg-green-950/40 border border-green-900/30 text-green-400 hover:bg-green-900 hover:text-white rounded-sm transition-all duration-300"
                                      title="Save Details"
                                    >
                                      <Check className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => setEditingId(null)}
                                      className="p-1.5 bg-gray-800/40 border border-gray-700/30 text-gray-400 hover:bg-gray-700 hover:text-white rounded-sm transition-all duration-300"
                                      title="Cancel"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-end gap-1.5">
                                    <button
                                      onClick={() => {
                                        setEditingId(item.id);
                                        setEditNameValue(item.product_name || '');
                                        setEditProductionTimeValue(item.production_time !== null && item.production_time !== undefined ? item.production_time.toString() : '');
                                      }}
                                      className="p-1.5 bg-[#1A1A1A] border border-[#27272A] text-gray-400 hover:text-[#FF9900] hover:border-[#FF9900]/55 rounded-sm transition-all duration-300"
                                      title="Edit Details"
                                    >
                                      <Edit2 className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={(e) => handleDeleteClick(item.id, e)}
                                      className="p-1.5 bg-red-950/40 border border-red-900/30 text-red-400 hover:bg-red-900 hover:text-white rounded-sm transition-all duration-300"
                                      title="Delete Draft"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* DELETE CONFIRMATION MODAL */}
      <AnimatePresence>
        {deleteTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDeleteTarget(null)}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            
            {/* Modal Box */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: 'spring', duration: 0.4 }}
              className="relative w-full max-w-md bg-[#121212] border border-[#27272A] p-6 shadow-2xl z-10"
            >
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 bg-red-950/30 border border-red-900/50 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-500" />
                </div>
              </div>

              <h3 className="text-lg font-bold text-center uppercase tracking-wider text-white mb-2">
                Delete Draft Photo?
              </h3>
              
              <p className="text-xs text-gray-400 text-center mb-6 leading-relaxed">
                Are you sure you want to permanently delete this product draft? This action cannot be undone and will remove the file from the server.
              </p>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setDeleteTarget(null)}
                  className="flex-1 bg-[#1e1e1e] hover:bg-gray-800 border border-[#27272A] text-gray-300 font-bold uppercase py-3 text-xs tracking-wider transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="flex-1 bg-red-900/40 text-red-400 border border-red-900/50 hover:bg-red-650 hover:text-white font-bold uppercase py-3 text-xs tracking-wider transition-all duration-300"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* LIGHTBOX MODAL */}
      <AnimatePresence>
        {activeZoomImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveZoomImage(null)}
            className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex flex-col items-center justify-center p-4 cursor-zoom-out"
          >
            {/* Close Button */}
            <button
              onClick={() => setActiveZoomImage(null)}
              className="absolute top-4 right-4 bg-[#121212] border border-[#27272A] p-2 hover:bg-white hover:text-black transition-colors rounded-sm text-gray-400 z-50 shadow-md"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Image Frame */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="relative max-w-5xl w-full h-[75vh] flex items-center justify-center p-4 overflow-hidden select-none"
            >
              <img
                src={activeZoomImage}
                alt="Zoomed view"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleMouseUp}
                style={{
                  transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomScale})`,
                  cursor: zoomScale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
                  transition: isDragging ? 'none' : 'transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (zoomScale === 1) {
                    setZoomScale(2);
                  } else {
                    setZoomScale(1);
                    setPanOffset({ x: 0, y: 0 });
                  }
                }}
                className="max-w-full max-h-full object-contain border border-[#27272A] rounded-sm shadow-2xl bg-[#0a0a0a]"
              />
            </motion.div>

            {/* Zoom Controls Bar */}
            <div 
              className="absolute bottom-6 bg-[#121212]/80 border border-[#27272A] backdrop-blur-md px-5 py-2.5 flex items-center gap-4 rounded-full z-50 shadow-xl select-none"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => {
                  setZoomScale(prev => Math.max(1, prev - 0.5));
                  if (zoomScale <= 1.5) setPanOffset({ x: 0, y: 0 });
                }}
                disabled={zoomScale <= 1}
                className="p-1 hover:text-[#FF9900] disabled:text-gray-600 disabled:hover:text-gray-600 transition-colors text-white text-xs font-black uppercase tracking-wider"
                title="Zoom Out"
              >
                Zoom Out (-)
              </button>
              <span className="text-xs font-black text-gray-400 select-none uppercase tracking-wider bg-black/40 border border-white/5 px-2.5 py-1 rounded-full min-w-[60px] text-center">
                {Math.round(zoomScale * 100)}%
              </span>
              <button
                type="button"
                onClick={() => setZoomScale(prev => Math.min(4, prev + 0.5))}
                disabled={zoomScale >= 4}
                className="p-1 hover:text-[#FF9900] disabled:text-gray-600 disabled:hover:text-gray-600 transition-colors text-white text-xs font-black uppercase tracking-wider"
                title="Zoom In"
              >
                Zoom In (+)
              </button>
              <div className="w-px h-4 bg-[#27272A]"></div>
              <button
                type="button"
                onClick={() => {
                  setZoomScale(1);
                  setPanOffset({ x: 0, y: 0 });
                }}
                disabled={zoomScale === 1 && panOffset.x === 0 && panOffset.y === 0}
                className="p-1 text-xs font-black uppercase tracking-wider hover:text-[#FF9900] disabled:text-gray-600 disabled:hover:text-gray-600 transition-colors text-white"
                title="Reset Zoom"
              >
                Reset
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WATERMARK CUSTOMIZATION MODAL */}
      <AnimatePresence>
        {isWatermarkModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCancelWatermark}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            
            {/* Modal Box */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: 'spring', duration: 0.4 }}
              className="relative w-full max-w-4xl bg-[#121212] border border-[#27272A] p-6 shadow-2xl z-10 flex flex-col md:flex-row gap-6 max-h-[90vh] overflow-y-auto"
            >
              {/* Left Side: Preview Canvas */}
              <div className="flex-1 flex flex-col items-center justify-center bg-[#0a0a0a] border border-[#27272A] p-4 rounded-sm min-h-[300px] relative overflow-hidden">
                <div className="absolute top-2 left-2 bg-[#FF9900]/10 border border-[#FF9900]/30 text-[#FF9900] text-[10px] font-bold px-2 py-0.5 tracking-wider uppercase rounded-sm z-10">
                  Live Preview
                </div>
                
                <button
                  type="button"
                  onClick={() => setIsPreviewZoomed(!isPreviewZoomed)}
                  className="absolute top-2 right-2 bg-[#1E1E1E]/90 hover:bg-[#27272A] border border-[#27272A] hover:border-[#FF9900]/50 text-white hover:text-[#FF9900] text-[10px] font-bold px-2.5 py-1 tracking-wider uppercase rounded-sm z-10 transition-all duration-300 flex items-center gap-1.5 shadow-lg"
                >
                  {isPreviewZoomed ? (
                    <>
                      <Minimize2 className="w-3.5 h-3.5" />
                      <span>Full View</span>
                    </>
                  ) : (
                    <>
                      <Maximize2 className="w-3.5 h-3.5" />
                      <span>Zoom Corner</span>
                    </>
                  )}
                </button>
                
                <canvas 
                  ref={previewCanvasRef}
                  style={{
                    ...getPreviewTransform(),
                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform-origin 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                  className="max-w-full max-h-[260px] md:max-h-[50vh] object-contain border border-[#27272A] shadow-md rounded-sm"
                />
                
                {pendingFiles.length > 1 && (
                  <div className="text-[10px] text-gray-500 mt-2 font-semibold">
                    Showing preview for first image. Watermark will be applied to all {pendingFiles.length} files.
                  </div>
                )}
              </div>

              {/* Right Side: Controls Panel */}
              <div className="w-full md:w-80 flex flex-col justify-between gap-6">
                <div>
                  <h3 className="text-md font-bold uppercase tracking-wider text-[#FF9900] mb-4 border-b border-[#27272A] pb-2 text-left">
                    Watermark Settings
                  </h3>

                  <div className="space-y-4 text-left">
                    {/* Text Input */}
                    <div>
                      <label className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1">Watermark Text</label>
                      <input
                        type="text"
                        value={watermarkText}
                        onChange={(e) => setWatermarkText(e.target.value)}
                        className="w-full bg-[#1E1E1E] border border-[#27272A] text-sm px-3 py-2 text-white focus:border-[#FF9900] focus:outline-none"
                        placeholder="Enter signature text"
                      />
                    </div>

                    {/* Font Dropdown */}
                    <div>
                      <label className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1">Font Style</label>
                      <select
                        value={watermarkFont}
                        onChange={(e) => setWatermarkFont(e.target.value)}
                        className="w-full bg-[#1E1E1E] border border-[#27272A] text-sm px-3 py-2 text-white focus:border-[#FF9900] focus:outline-none"
                      >
                        <option value="Oswald">Oswald (EI Headers)</option>
                        <option value="Manrope">Manrope (EI Body)</option>
                        <option value="Arial">Arial (Sans-Serif)</option>
                        <option value="Georgia">Georgia (Serif)</option>
                        <option value="Courier New">Courier New (Monospace)</option>
                      </select>
                    </div>

                    {/* Location Select */}
                    <div>
                      <label className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1">Location</label>
                      <div className="grid grid-cols-2 gap-2">
                        {['top-left', 'top-right', 'bottom-left', 'bottom-right'].map(loc => (
                          <button
                            key={loc}
                            type="button"
                            onClick={() => setWatermarkLocation(loc)}
                            className={`py-1.5 px-2 text-[10px] font-bold uppercase tracking-wider border rounded-sm transition-all duration-300 ${
                              watermarkLocation === loc
                                ? 'bg-[#FF9900]/10 border-[#FF9900] text-[#FF9900]'
                                : 'bg-[#1E1E1E] border-[#27272A] text-gray-400 hover:text-white'
                            }`}
                          >
                            {loc.replace('-', ' ')}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Size Slider */}
                    <div>
                      <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider mb-1">
                        <span className="text-gray-400">Size</span>
                        <span className="text-[#FF9900]">{watermarkSize}%</span>
                      </div>
                      <input
                        type="range"
                        min="1"
                        max="12"
                        step="0.5"
                        value={watermarkSize}
                        onChange={(e) => setWatermarkSize(parseFloat(e.target.value))}
                        className="w-full accent-[#FF9900] cursor-pointer"
                      />
                    </div>

                    {/* Padding Slider */}
                    <div>
                      <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider mb-1">
                        <span className="text-gray-400">Padding</span>
                        <span className="text-[#FF9900]">{watermarkPadding}%</span>
                      </div>
                      <input
                        type="range"
                        min="0.5"
                        max="10"
                        step="0.5"
                        value={watermarkPadding}
                        onChange={(e) => setWatermarkPadding(parseFloat(e.target.value))}
                        className="w-full accent-[#FF9900] cursor-pointer"
                      />
                    </div>

                    {/* Opacity Slider */}
                    <div>
                      <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider mb-1">
                        <span className="text-gray-400">Opacity</span>
                        <span className="text-[#FF9900]">{watermarkOpacity}%</span>
                      </div>
                      <input
                        type="range"
                        min="10"
                        max="100"
                        value={watermarkOpacity}
                        onChange={(e) => setWatermarkOpacity(parseInt(e.target.value))}
                        className="w-full accent-[#FF9900] cursor-pointer"
                      />
                    </div>

                    {/* Color Presets and Picker */}
                    <div>
                      <label className="block text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1">Text Color</label>
                      <div className="flex items-center gap-2">
                        {['#FFFFFF', '#CCCCCC', '#333333', '#FF9900'].map(color => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setWatermarkColor(color)}
                            style={{ backgroundColor: color }}
                            className={`w-6 h-6 border rounded-sm transition-all duration-300 ${
                              watermarkColor === color ? 'border-[#FF9900] scale-110 ring-1 ring-[#FF9900]' : 'border-[#27272A]'
                            }`}
                            title={color}
                          />
                        ))}
                        <input
                          type="color"
                          value={watermarkColor}
                          onChange={(e) => setWatermarkColor(e.target.value)}
                          className="w-8 h-8 bg-transparent border-0 cursor-pointer p-0"
                          title="Custom Color"
                        />
                      </div>
                    </div>

                    {/* Outline Border Controls */}
                    <div className="border-t border-[#27272A] pt-4 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer select-none mb-3 group">
                        <input
                          type="checkbox"
                          checked={watermarkBorder}
                          onChange={(e) => setWatermarkBorder(e.target.checked)}
                          className="peer sr-only"
                        />
                        <div className="w-4 h-4 border border-white/60 bg-[#121212] group-hover:border-[#FF9900] peer-checked:border-[#FF9900] peer-checked:bg-[#FF9900] flex items-center justify-center transition-all duration-300 rounded-sm">
                          <Check className="w-3 h-3 text-black font-black opacity-0 peer-checked:opacity-100 transition-opacity" />
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 group-hover:text-white transition-colors">
                          Enable Text Outline (Border)
                        </span>
                      </label>

                      {watermarkBorder && (
                        <div className="flex flex-col gap-3 bg-[#1e1e1e]/60 border border-[#27272A] p-3 rounded-sm">
                          <div>
                            <label className="block text-[9px] text-gray-500 font-bold uppercase tracking-wider mb-1">Outline Color</label>
                            <div className="flex items-center gap-1.5">
                              {['#000000', '#FFFFFF', '#333333'].map(color => (
                                <button
                                  key={color}
                                  type="button"
                                  onClick={() => setWatermarkBorderColor(color)}
                                  style={{ backgroundColor: color }}
                                  className={`w-5 h-5 border rounded-sm transition-all duration-300 ${
                                    watermarkBorderColor === color ? 'border-[#FF9900] scale-110' : 'border-[#27272A]'
                                  }`}
                                  title={color}
                                />
                              ))}
                              <input
                                  type="color"
                                  value={watermarkBorderColor}
                                  onChange={(e) => setWatermarkBorderColor(e.target.value)}
                                  className="w-6 h-6 bg-transparent border-0 cursor-pointer p-0"
                                  title="Custom Outline Color"
                              />
                            </div>
                          </div>

                          <div className="border-t border-[#27272A]/50 pt-2">
                            <div className="flex justify-between text-[9px] font-bold uppercase tracking-wider mb-1 font-family-sans">
                              <span className="text-gray-500">Outline Size</span>
                              <span className="text-[#FF9900]">{watermarkBorderSize}%</span>
                            </div>
                            <input
                              type="range"
                              min="5"
                              max="35"
                              step="1"
                              value={watermarkBorderSize}
                              onChange={(e) => setWatermarkBorderSize(parseInt(e.target.value))}
                              className="w-full accent-[#FF9900] cursor-pointer"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Confirm/Cancel Buttons */}
                <div className="flex flex-col gap-2 border-t border-[#27272A] pt-4">
                  <button
                    type="button"
                    onClick={handleConfirmWatermark}
                    className="w-full bg-[#FF9900] text-black font-bold uppercase py-3 text-xs tracking-wider hover:bg-white transition-all duration-300 rounded-sm"
                  >
                    Apply & Upload
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelWatermark}
                    className="w-full bg-[#1e1e1e] hover:bg-gray-800 border border-[#27272A] text-gray-300 font-bold uppercase py-3 text-xs tracking-wider transition-colors rounded-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EmployeeUploadPage;
